/**
 * A tab left open across a deploy still points at chunk files the new build has renamed, so the
 * next lazy import fails. One reload fetches the new build, but only when the server really has one:
 * a chunk that fails for any other reason (a network blip, a file that is simply missing) says nothing
 * about a deploy, and reloading then loses picked photos and breaks the caller's own retry. A reload
 * inside RELOAD_WINDOW_MS of the last one is refused too, so a shell that keeps changing cannot loop.
 * Nothing she typed is lost to it: `recoverFromStaleBuild` saves first.
 */
const KEY = 'tt-chunk-reload'
const RELOAD_WINDOW_MS = 60_000

const CHUNK_FAILURE =
	/Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Loading (CSS )?chunk [\w-]+ failed|Unable to preload CSS/i

export function isChunkLoadError(err: unknown): boolean {
	const message = err instanceof Error ? err.message : typeof err === 'string' ? err : ''
	return CHUNK_FAILURE.test(message)
}

type Guard = Pick<Storage, 'getItem' | 'setItem'>

/** True when the caller should reload now; records that it did. Storage that throws counts as "already tried". */
export function claimReload(storage: Guard, now = Date.now()): boolean {
	try {
		const last = Number(storage.getItem(KEY))
		if (last && now - last < RELOAD_WINDOW_MS) return false
		storage.setItem(KEY, String(now))
		return true
	} catch {
		return false
	}
}

/**
 * The entry script a shell page names: the first `<script type="module" src>`, which the build gives a
 * new hash on every deploy. Null when the page names none (an error page, a proxy's page).
 */
export function entryOf(html: string): string | null {
	for (const tag of html.matchAll(/<script\b[^>]*>/gi)) {
		if (!/\btype="module"/i.test(tag[0])) continue
		const src = /\bsrc="([^"]+)"/i.exec(tag[0])?.[1]
		if (src) return src
	}
	return null
}

/** A new build is confirmed only when both entries are known and differ; anything unknown is not a deploy. */
export const isNewBuild = (running: string | null, onServer: string | null): boolean =>
	running !== null && onServer !== null && running !== onServer

/**
 * `reloaded`: the page is on its way. `same`: the server has no newer build (or it could not be asked),
 * so the failure is not a deploy and the caller's own handling stands. `tried`: it reloaded a moment ago,
 * so it will not again. `unsaved`: edits could not be saved, so it did not reload.
 */
export type Recovery = 'reloaded' | 'same' | 'tried' | 'unsaved'

/**
 * The one way a stale build ends in a reload, for the router's error screen and the page-wide
 * listeners alike. Only a confirmed new build reloads. Her edits are saved first, and if they cannot be
 * (the server refused them, or the connection is gone) the page stays as it is: an unsaved edit
 * outranks a stale chunk.
 */
export async function recoverFromStaleBuild(deps: {
	/** Whether the server now serves a newer build than the one running; false when unsure. */
	newBuild: () => Promise<boolean>
	storage: Guard
	/** Saves what she has typed; true when nothing is left unsaved. */
	saved: () => Promise<boolean>
	reload: () => void
	now?: number
}): Promise<Recovery> {
	if (!(await deps.newBuild().catch(() => false))) return 'same'
	const clean = await deps.saved().catch(() => false)
	if (!clean) return 'unsaved'
	if (!claimReload(deps.storage, deps.now)) return 'tried'
	deps.reload()
	return 'reloaded'
}

/**
 * A route whose own code would not load reloads once even without a new build: whatever broke the
 * import (a dropped connection, a request the browser cancelled), the error screen is all that is
 * left, so a reload costs nothing. Edits are saved first, never offline (the browser's own error page
 * is worse than ours), and at most once a minute, so a file that is really missing cannot loop.
 */
export async function retryRouteLoad(deps: {
	online: boolean
	storage: Guard
	saved: () => Promise<boolean>
	reload: () => void
	/** When this page began loading (performance.timeOrigin). */
	pageStart: number
	now?: number
}): Promise<boolean> {
	if (!deps.online) return false
	// This page is itself the retry: requests that stall for a minute must not reload forever.
	try {
		const last = Number(deps.storage.getItem(KEY))
		if (last && deps.pageStart - last < 10_000) return false
	} catch {
		return false
	}
	if (!(await deps.saved().catch(() => false))) return false
	if (!claimReload(deps.storage, deps.now)) return false
	deps.reload()
	return true
}
